import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { AppShell } from '../../components/AppShell';
import { CustomerCard } from '../../components/CustomerCard';
import { PhotoGrid } from '../../components/PhotoGrid';
import { PhotoPicker } from '../../components/PhotoPicker';
import { Spinner } from '../../components/Spinner';
import { StatusChip } from '../../components/StatusChip';
import { Button } from '../../components/Button';
import { apiGet, apiUpload, ClientApiError } from '../../api/client';
import { formatDateTime } from '../../lib/format';
import { downscaleImage } from '../../lib/image';
import { decidePhotoBatchOutcome } from '../../lib/photo-batch';
import { remainingPhotoBudget } from '../../lib/photo-budget';
import { MAX_PHOTOS } from '../../../shared/validation';
import type { Referral, ReferralPhoto } from '../../../shared/types';

interface Detail {
  referral: Referral;
  photos: ReferralPhoto[];
}

const STEPS = [
  { key: 'created', label: 'Submitted', at: (r: Referral) => r.createdAt },
  { key: 'assigned', label: 'In Progress', at: (r: Referral) => r.assignedAt },
  { key: 'complete', label: 'Complete', at: (r: Referral) => r.completedAt },
];

export function PlumberReferralScreen() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const photoWarning = (location.state as { photoWarning?: number } | null)?.photoWarning;
  const [detail, setDetail] = useState<Detail | null>(null);
  const [error, setError] = useState('');
  const [addFiles, setAddFiles] = useState<File[]>([]);
  const [addBanner, setAddBanner] = useState('');
  const [addProgress, setAddProgress] = useState('');
  const [addBusy, setAddBusy] = useState(false);

  useEffect(() => {
    apiGet<Detail>(`/api/referrals/${id}`)
      .then(setDetail)
      .catch((caught) =>
        setError(caught instanceof ClientApiError ? caught.message : 'Could not load that referral.'),
      );
  }, [id]);

  async function reload() {
    try {
      const next = await apiGet<Detail>(`/api/referrals/${id}`);
      setDetail(next);
    } catch {
      // The photos already uploaded either way; a failed refresh just means
      // the grid below won't show them until the plumber reloads the page.
    }
  }

  async function addPhotos() {
    if (addFiles.length === 0) return;
    setAddBanner('');
    setAddBusy(true);

    // Mirrors FieldPhotoUploader's use of decidePhotoBatchOutcome: a 403/404/409
    // means the referral moved on (or access to it changed) mid-batch, so every
    // remaining file is doomed the same way and reporting a failure count would
    // be misleading. Stop, reload so canAddPhotos re-evaluates against the new
    // status, and say plainly what happened instead.
    let shouldReload = false;
    let failed = 0;
    let stopMessage = '';

    for (let index = 0; index < addFiles.length; index++) {
      setAddProgress(`Uploading photo ${index + 1} of ${addFiles.length}`);
      try {
        const blob = await downscaleImage(addFiles[index]!);
        await apiUpload(`/api/referrals/${id}/photos`, blob, `photo-${index + 1}.jpg`);
        shouldReload = true;
      } catch (caught) {
        const decision = decidePhotoBatchOutcome(caught);
        if (decision.reload) shouldReload = true;
        if (decision.countFailed) failed += 1;
        if (decision.stop) {
          stopMessage = decision.reload
            ? 'This referral has been picked up by the Refer Now team, so photos can no longer be added.'
            : caught instanceof ClientApiError
              ? caught.message
              : 'A photo did not upload. Try again below.';
          break;
        }
      }
    }

    setAddFiles([]);
    setAddProgress('');
    if (shouldReload) {
      await reload();
    }

    if (stopMessage) {
      setAddBanner(stopMessage);
    } else if (failed > 0) {
      setAddBanner(
        failed === 1
          ? 'One photo did not upload. Try again below.'
          : `${failed} photos did not upload. Try again below.`,
      );
    }
    setAddBusy(false);
  }

  if (error) {
    return (
      <AppShell title="Referral">
        <div className="error-banner" role="alert">
          {error}
        </div>
        <Link to="/referrals">Back to my referrals</Link>
      </AppShell>
    );
  }

  if (!detail) {
    return (
      <AppShell title="Referral">
        <Spinner />
      </AppShell>
    );
  }

  const { referral, photos } = detail;
  const canAddPhotos = referral.status === 'submitted';
  const budget = remainingPhotoBudget(photos.length);

  return (
    <AppShell title="Referral">
      {photoWarning ? (
        <div className="error-banner" role="alert">
          {photoWarning === 1
            ? 'One photo did not upload. The referral was still sent.'
            : `${photoWarning} photos did not upload. The referral was still sent.`}
          {canAddPhotos
            ? photoWarning === 1
              ? ' You can add it below.'
              : ' You can add them below.'
            : ''}
        </div>
      ) : null}

      <div className="row" style={{ justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <strong style={{ color: 'var(--navy)' }}>Status</strong>
        <StatusChip status={referral.status} plumberView />
      </div>

      <CustomerCard referral={referral} />

      <div className="card">
        <h2>Progress</h2>
        {referral.status === 'declined' ? (
          <div>
            <p className="muted" style={{ marginBottom: referral.declineReason ? '0.4rem' : 0 }}>
              This referral was not accepted.
            </p>
            {referral.declineReason && (
              <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>Reason: {referral.declineReason}</p>
            )}
          </div>
        ) : (
          <ol style={{ margin: 0, paddingLeft: '1.1rem' }}>
            {STEPS.map((step) => {
              const at = step.at(referral);
              return (
                <li key={step.key} style={{ color: at ? 'var(--ink)' : 'var(--muted)' }}>
                  {step.label}
                  {at && <span className="muted"> · {formatDateTime(at)}</span>}
                </li>
              );
            })}
          </ol>
        )}
      </div>

      <div className="card">
        <h2>Your photos</h2>
        <PhotoGrid photos={photos} />

        {addBanner && (
          <div className="error-banner" role="alert" style={{ marginTop: '1rem' }}>
            {addBanner}
          </div>
        )}

        {canAddPhotos ? (
          <div style={{ marginTop: '1rem' }}>
            {budget > 0 ? (
              <>
                <PhotoPicker files={addFiles} onChange={setAddFiles} max={budget} />

                {addProgress && (
                  <p className="muted" role="status">
                    {addProgress}
                  </p>
                )}

                {addFiles.length > 0 && (
                  <Button
                    type="button"
                    full
                    disabled={addBusy}
                    onClick={() => void addPhotos()}
                    style={{ marginTop: '0.5rem' }}
                  >
                    {addBusy ? 'Uploading' : `Upload ${addFiles.length} photo${addFiles.length === 1 ? '' : 's'}`}
                  </Button>
                )}
              </>
            ) : (
              <p className="muted">This referral has reached the {MAX_PHOTOS} photo limit.</p>
            )}
          </div>
        ) : null}
      </div>
    </AppShell>
  );
}
